import React from 'react';

import {
  LeafType,
  isBenchlingTypeDefComplex,
  isBenchlingTypeDefList,
} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {EntityWithTextAndSuspended} from 'ui-library/entity-input/EntityInputConstants';
import {
  EntityInputWithSearch,
  EntityInputWithSearchProps,
} from 'ui-library/entity-input/EntityInputWithSearch';
import {UserSearchType} from 'ui-library/entity-input/EntityInputWithSearch';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';

import {LeafTypeToValueEditorTSType, ValueInputProps} from './ValueInputTypes';

const USER_INPUT_PROPS: Partial<EntityInputWithSearchProps> = {
  allowTeams: false,
  allowEmails: false,
  allowBenchlingApps: false,
  allowOrganizations: false,
};

const TEAM_INPUT_PROPS: Partial<EntityInputWithSearchProps> = {
  allowTeams: true,
  allowEmails: false,
  allowBenchlingApps: false,
  allowOrganizations: false,
  userSearchType: UserSearchType.None,
};

/**
 * @package
 */
function PartyValueInput<TLeafType extends LeafType.USER | LeafType.TEAM>({
  allowAutoFocus,
  typeDef,
  valueInput,
  onChange,
  disabled,
}: ValueInputProps<TLeafType>): JSX.Element {
  const value = valueInput.isValid ? valueInput.value : null;

  function handleOnChange(entities: Array<EntityWithTextAndSuspended>): void {
    const selectedIds: LeafTypeToValueEditorTSType[TLeafType] = entities.map(({id}) => id);

    if (isBenchlingTypeDefList(typeDef)) {
      onChange(selectedIds);
    } else {
      onChange(selectedIds[0] ?? null);
    }
  }

  const valueAsArray: Array<string> = Array.isArray(value)
    ? value
    : [value].filter((v): v is string => v != null);
  const leafType = isBenchlingTypeDefComplex(typeDef) ? typeDef.getBaseTypes()[0] : typeDef;

  let propsForEntityType;
  switch (leafType) {
    case LeafType.USER:
      propsForEntityType = USER_INPUT_PROPS;
      break;
    case LeafType.TEAM:
      propsForEntityType = TEAM_INPUT_PROPS;
      break;
    default:
      throw new Error(`Unexpected entity type ${leafType}`);
  }

  return (
    <EntityInputWithSearch
      {...propsForEntityType}
      autoFocus={allowAutoFocus}
      enableNewEntityInput={true}
      onChange={handleOnChange}
      shouldAllowMultiple={isBenchlingTypeDefList(typeDef)}
      size={TagsInputSize.MEDIUM}
      value={valueAsArray.map((id) => ({id, handle: ''}))}
      disabled={disabled}
      isReadOnly={disabled} // TODO(BNCH-57668): keep disabled and isReadOnly separate when behavior is fixed
    />
  );
}

/**
 * @package
 */
export function TeamValueInput(props: ValueInputProps<LeafType.TEAM>): JSX.Element {
  return <PartyValueInput {...props} />;
}

/**
 * @package
 */
export function UserValueInput(props: ValueInputProps<LeafType.USER>): JSX.Element {
  return <PartyValueInput {...props} />;
}
